/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, destroy, getEnv } from 'mobx-state-tree';
import * as mobx from 'mobx';
import StatsModel, { SelectionStatsModel }  from './StatsModel';
import RefChecker from 'lib/@waca/ref-checker/src/js/RefChecker';

import StatsProcessor from './StatsProcessor';
import MatchScaleProcessor from './MatchScaleProcessor';
import SynchActionProcessor from './SynchActionProcessor';

import CompareTableSummary from './CompareTableSummary';

import StringResources from 'nls/StringResources';

export const StatsModelMap =  types.union( types.map( StatsModel ), types.undefined);
export const SelectionStatsModelMap =  types.union( types.map( SelectionStatsModel ), types.undefined);

import CompareCardAPI from 'api/CompareCardAPI';

const disconnectWidgetsFromGroup = (dashboardApi, widgetIds) => {
	if (widgetIds && widgetIds.length > 0) {
		const feature = dashboardApi.getFeature('EventGroups');
		const oldGroupId = feature.getGroupId(widgetIds[0]);
		feature.removeFromGroup(widgetIds);
		const disposer = () => {
			if (feature.groupExists(oldGroupId)) {
				feature.addToGroup(oldGroupId, widgetIds);
			} else {
				feature.createGroup(widgetIds);
			}
		};
		return disposer;
	} else {
		return () => {};
	}
};

/**
* Class representing comparison state
* It holds 2 maps statsModelMap, selStatsModelMap + other state
* statsModelMap -->  <widget id (string)> -> <StatsModel>
* 	It keeps stats received from live widget when there are no selections (default stats)
* selStatsModelMap -->  <widget id (string)> -> <StatsModel>
*	It keeps stats received from live widget when there are selections
*
* This class centralizes state for compare functionality, so that views can render the info
* Also, the selections info feeds into another model (SelectionGistModel) which provides 'computed view' on top of
* the selection data so Compare functionality can determine comparable data & compute additional calculated stats
* e.g. total, average etc..
*/
export default types.model({
	statsModelMap: StatsModelMap,
	selStatsModelMap: SelectionStatsModelMap
}).volatile((self) => ({
	showCompareTable: true,

	showCompareLine: true,
	showStats: true,
	matchingPoints: mobx.observable.map({}),
	renderCrosshairPopOver: '',
	syncWidgetSelectionsUpdated: 0,	//A flag which changes whenever the synchSelections toggle needs to be updated.
	syncCompareLine: true,
	matchScale: true,
	isToggledBySyncAxisButton: false,
	syncActions: true,
	widgetMeasureNameMap: {},	//A map of widgetIds - selectedMeasureNames (as selected in the compare table summary.)
	eventGroups: null,
	widgetAliasMap: {},
	widgetIds: [],
	widgetContentApisMap: {},	//APIs for the widgets in this compare model (used for synchronized actions)
	synchActionProcessor: null,
	theMatchScaleProcessor: MatchScaleProcessor,
	theStatsProcessor: StatsProcessor,
	compareTableSummary: null,
	compareTableSelectionSummary: null,
	statsUpdated: 0,
	selectionUpdated: 0,
	widgetUpdatedCount: 0,
	refChecker: null,
	theStringResources: StringResources,
	_mobxDisposeEvents: [],
	_api : null,

	getAPI: () => {
		return self._api;
	}
})).views((self) => ({
	getStatsModel(widgetId) {
		return self.statsModelMap && self.statsModelMap.get(widgetId);
	},

	getSelectionStatsModel(widgetId) {
		return self.selStatsModelMap && self.selStatsModelMap.get(widgetId);
	},

	getNewCompareTableStatsEnabled() {
		const env = getEnv(self);
		return env && env.isFeatureEnabled && !env.isFeatureEnabled('newCompareTableStatsEnabled', 'false');
	},

	getChartSummaryModel( category ) {
		if(category === 'summary') {
			return self.compareTableSummary;
		} else if( category === 'selections') {
			return self.compareTableSelectionSummary;
		}
		return undefined;
	},

	summaryStatsGetter(category) {
		if(category === 'summary') {
			return self.getChartSummaryStats.bind(self);
		} else if( category === 'selections') {
			return self.getChartSelectionSummaryStats.bind(self);
		}
		return undefined;
	},

	summarySubHeaderGetter(category) {
		if( category === 'selections') {
			return self.getSelectionContextLabel.bind(self);
		}
		return () => null;
	},

	getChartSummaryStats(widgetId, measureName) {
		let stats = null;
		const statsModel = self.getStatsModel(widgetId);
		if(statsModel) {
			const chartVsChart = self.chartVsChartTotalPercentStats[widgetId];
			const widgetAlias = self.getWidgetAlias(widgetId);
			const getString = StringResources.get.bind(StringResources);
			const newCompareTableStatsEnabled = self.getNewCompareTableStatsEnabled();
			stats = self.theStatsProcessor.getChartSummaryStats({ newCompareTableStatsEnabled, statsModel, chartVsChart, measureName, widgetAlias, getString });
		}
		return stats;
	},

	getChartSelectionSummaryStats(widgetId, measureName) {
		let stats = null;
		const statsModel = self.getStatsModel(widgetId);
		const selStatsModel = self.getSelectionStatsModel(widgetId);
		if(statsModel) {
			const widgetAlias = self.getWidgetAlias(widgetId);
			const getString = self.theStringResources.get.bind(self.theStringResources);
			const newCompareTableStatsEnabled = self.getNewCompareTableStatsEnabled();
			stats = self.theStatsProcessor.getChartSelectionSummaryStats({ newCompareTableStatsEnabled, statsModel, selStatsModel, measureName, widgetAlias, getString });
		}
		return stats;
	},

	getSelectionContextLabel(widgetId, measureName) {
		const selStatsModel = self.getSelectionStatsModel(widgetId);
		if( selStatsModel ) {
			const info = self.theStatsProcessor.getSelectionContextLabel(selStatsModel, measureName);
			let str = info.lastCategorySelectionLabel;
			if( info.numSelections > 1 ) {
				const params = { selection:info.lastCategorySelectionLabel, count: info.numSelections-1 };
				str = self.theStringResources.get('compareTable_Selection_SubLabel', params);
			}
			return [ str ];
		}
		return undefined;
	},

	get chartVsChartTotalPercentStats() {
		return self.theStatsProcessor.getChartVsChartTotalPercentStats(self.statsModelMap );
	},

	getWidgetAlias(widgetId) {
		return self.widgetAliasMap[widgetId] || '';
	},

	getWidgetContentApis() {
		return Object.values(self.widgetContentApisMap);
	},

	getWidgetMeasureName(widgetId) {
		return self.widgetMeasureNameMap[widgetId];
	},

	/**
	 * @return {Object} the matching points for compare cards
	 */
	getCrossHairMatchingPoints() {
		return mobx.toJS(self.matchingPoints);
	},

	/**
	 * Renders a popover with the given parameters when called.
	 * @param {Object} crossHairInfo - the info to show inside the popover
	 * @param {Object} renderNode - the node to reder the popover to
	 * @param {Number} totalItems - the number of rows in the popover
	 * @param {Boolean} footerinfo.showFooter - true to show footer, false to hide footer
	 * @param {Object} containerNode - container node
	 */
	renderPopOver(crossHairInfo, renderNode, totalItems, footerinfo, containerNode) {
		self.renderCrosshairPopOver && self.renderCrosshairPopOver(crossHairInfo, renderNode, totalItems, footerinfo, containerNode);

	},

	/**
	 * check if the compare line is enabled. The compare line is only disabled when the flag is set to false specifically.
	 * @returns {Boolean} true if enabled
	 */
	isNewCompareLineFeatureEnabled() {
		return !getEnv(self).isFeatureEnabled('newCompareLine', 'false');
	},

	isWidgetSelectionsSynced() {
		if (self.syncWidgetSelectionsUpdated && self.eventGroups) {
			//Recalculate widget synched based on how many event groups the widgets are assigned to.
			//ie: if they're both assigned to 1 group, synch selections toggle should be true.
			const eventGroupSet={};
			self.widgetIds.forEach((widgetId) => {
				const group = self.eventGroups.findGroup(widgetId);
				if (group) {
					eventGroupSet[group.id]=true;
				}
			});
			return Object.keys(eventGroupSet).length < 2 ? true : false;
		}
		return true;
	},

	/**
	 * @param widgetId - the widgetId.
	 * @return true if widget with id widgetId has data.
	 */
	widgetHasData(widgetId) {
		const mdl = self.statsModelMap.get(widgetId);
		return mdl && mdl.measures && mdl.measures.size > 0;
	},

	/**
	 * @return the number of widgets in this compare card that currently have data to show.
	 */
	get numberOfWidgetsWithData() {
		let widgetsWithDataCount = 0;
		if (self.statsUpdated) {
			self.widgetIds.forEach((widgetId) => {
				widgetsWithDataCount+=(self.widgetHasData(widgetId)) ? 1 : 0;
			});
		}
		return widgetsWithDataCount;
	},

	/**
	 * The compareline is enabled when at least one widget supports a compare line
	 * AND that widget has data.  Note: The compare line is also known as crosshairs.
	 * Update the model based on the rules.
	 */
	get compareLineEnabled() {
		if (self.statsUpdated) {
			return self.getWidgetContentApis().some((widgetContentApi) => {
				const crosshairsFeature = widgetContentApi.getFeature('WidgetAPI.deprecated').getFeature('vis-crosshairs');
				if (crosshairsFeature && crosshairsFeature.isSupported()) {
					return self.widgetHasData(widgetContentApi.getId());
				}
			});
		}
		return false;
	},

	/**
	 * @return true if the compare line should be enabled
	 */
	isCompareLineEnabled() {
		return self.compareLineEnabled;
	},

	/**
	 * @return true if the compare table should be enabled (ie: 1 widget has data)
	 */
	isCompareTableEnabled() {
		return self.numberOfWidgetsWithData > 0;
	},

	/**
	 * @return true if the sync menu should be enabled (ie: 2 widgets have data)
	 */
	isSyncMenuEnabled() {
		return self.numberOfWidgetsWithData > 1;
	},

	isScaleSynced() {
		return self.matchScale;
	},

	isCompareLineSynced() {
		return self.syncCompareLine;
	},

	isActionsSynced() {
		return self.syncActions;
	},

	isCompareLineShown() {
		return self.showCompareLine;
	},

	//@TODO Story 271817
	isRelativeTime(){
		return false;
	},

	getWidgetSubLabel(widgetId) {
		const filterInfo = self.widgetFilterInfo[widgetId];
		if( filterInfo ) {
			const subLabel = [];
			filterInfo.forEach( (item) => {
				const itemStr = self.theStringResources.get('compareChartSubLabel', { title: item.title, description: item.description });
				subLabel.push( itemStr );
			} );
			return subLabel.join(' , ');
		}
		return undefined;
	},

	/**
	 * determines the sync match scale's button should be on or off by default
	 * on if same measure and same aggregation or it is relative time
	 * @param {object} domains - a map of domains
	 * @param {object} widgetApi - widget api of the source widget
	 * @returns {boolean}
	 */
	getDefaultMatchScaleOption(domains, widgetContentApi){
		return self.theMatchScaleProcessor.shouldMatchingScaleByDefault(domains, widgetContentApi, self.widgetIds) || self.isRelativeTime();
	},

	/**
	 * @returns if the matching scale is allowed (enable or disabled state for sync axis button UI)
	 */
	get isMatchScaleEnabled() {
		return self.theMatchScaleProcessor.allowMatchingScale(self.domain, self.widgetIds);
	}
})).actions(self => ({
	afterCreate() {
		self.refChecker = new RefChecker(self);
		self.synchActionProcessor = new SynchActionProcessor(self, self.refChecker);
		self.compareTableSummary = CompareTableSummary.create({ applicableCalcs: undefined, charts: undefined }, { isFeatureEnabled: getEnv(self).isFeatureEnabled } );
		self.compareTableSummary.setWidgetMeasureNameMap(self.widgetMeasureNameMap);
		self.compareTableSelectionSummary = CompareTableSummary.create({ applicableCalcs: undefined, charts: undefined }, { isFeatureEnabled: getEnv(self).isFeatureEnabled } );
		self.compareTableSelectionSummary.setWidgetMeasureNameMap(self.widgetMeasureNameMap);

		self.widgetFilterInfo = {};

		self.syncWidgetSelectionsDisposer = null;

		self._api = new CompareCardAPI(self);
	},

	setStatsProcessor(processor) {
		self.theStatsProcessor = processor;
	},

	setStringResources(resources) {
		self.theStringResources = resources;
	},

	setShowCompareLine(flag) {
		self.matchingPoints.clear();
		self.showCompareLine = flag;
	},

	setShowStats(flag) {
		self.showStats = flag;
	},

	setCompareLineSynced(value) {
		self.syncCompareLine = value;
	},

	setScaleSynced(value, isToggledBySyncAxisButton) {
		self.matchScale = value;
		self.isToggledBySyncAxisButton = isToggledBySyncAxisButton;
	},

	setActionsSynced(value) {
		if (value) {
			self.synchActionProcessor.enable();
		} else {
			self.synchActionProcessor.disable();
		}
		self.syncActions = value;
	},

	setWidgetSelectionsSyncUpdated() {
		//force a change of the value of this flag so that the isWidgetSelectionsSynced calculation will be performed.
		self.syncWidgetSelectionsUpdated = self.syncWidgetSelectionsUpdated + 1;
	},

	setEventGroups(eventGroups) {
		self.eventGroups = eventGroups;
	},

	/**
	 * This action is called by the synch selections toggle (see exploreCompareOptions toolbar)
	 * @param synchronizeState (bool) - false = split widgets into separate event groups...selections will not be synchronized.
	 * 																	true - combine the widgets into one group (synchronizeState=true).
	 * Once the event groups have been modified,
	 * it calls setWidgetSelectionsSyncUpdated to ensure isWidgetSelectionSynced is recalculated
	 * and the toggle for the compare option react control is updated immediately.
	 * @returns a Promise which is resolved when event groups have been modified.
	*/
	updateWidgetSelectionsSynced(synchronizeState) {
		if( synchronizeState === false || !self.syncWidgetSelectionsDisposer) {
			const widgetIds = self.widgetIds;
			const disconnect = () => {
				self.syncWidgetSelectionsDisposer = disconnectWidgetsFromGroup(self.dashboardApi, widgetIds);

				if (synchronizeState && self.syncWidgetSelectionsDisposer) {
					//If sync was off initially, the widgets are in different event groups.
					//disconnect them (to remove them from the multiple groups), then call connect to put them into the same group (group 1)
					self.syncWidgetSelectionsDisposer();
					self.syncWidgetSelectionsDisposer = null;
				}

				self.setWidgetSelectionsSyncUpdated();
				//The widgets may or may not have changed as a result of changing event groups.
				//
				self.getWidgetContentApis().forEach((widgetContentApi) => widgetContentApi.getFeature('WidgetAPI.deprecated').render());
				return {};
			};
			return disconnect();
		} else {
			if(self.syncWidgetSelectionsDisposer) {
				self.syncWidgetSelectionsDisposer();
				self.syncWidgetSelectionsDisposer = null;
				self.setWidgetSelectionsSyncUpdated();
				//The widgets may or may not have changed as a result of changing event groups.
				self.getWidgetContentApis().forEach((widgetContentApi) => widgetContentApi.getFeature('WidgetAPI.deprecated').render());
			}
			return Promise.resolve({});
		}
	},

	updateSyncAxisScale(value, isMatchScaleForcedByUser) {
		self.setScaleSynced(value, isMatchScaleForcedByUser);
		const taskCompletePromises = [];
		self.widgetIds.forEach(id => taskCompletePromises.push(self.taskComplete(id))); // re-render
		return Promise.all(taskCompletePromises);
	},

	toggleShowCompareLine() {
		const oldValue = self.isCompareLineShown();
		self.setShowCompareLine(!oldValue);
		self.getWidgetContentApis().forEach((widgetContentApi) => {
			const crosshairsFeature = widgetContentApi.getFeature('WidgetAPI.deprecated').getFeature('vis-crosshairs');
			if(crosshairsFeature) {
				if (oldValue) {
					crosshairsFeature.deactivate();
				} else {
					const widgetContentId = widgetContentApi.getId();
					self._activateCrosshairs(widgetContentId);
					if (crosshairsFeature.isSupported()) {
						crosshairsFeature.renderCrosshairs();
						// calling renderCrosshairs rendered compare line into the position which value is `crosshairsFeature.getOptimalLineValue()`
						// so, after rendering compare line into the position, we render the popover which shows the matching points of this position
						self._setupPopover(crosshairsFeature, widgetContentId, crosshairsFeature.getOptimalLineValue());
					}
				}
			}
		});
	},

	toggleShowCompareTable() {
		self.showCompareTable = !self.showCompareTable;
	},

	addStatsModels(widgetId, initialModel, initialSelModel, measureLabel, widgetAlias ='') {
		const env = {
			measureLabel: measureLabel,
			showStats : () => { return self.showStats; },
			matchScale: () => { return self.matchScale; }
		};

		if (!self.widgetIds.includes(widgetId)) {
			self.widgetIds.push(widgetId);
		}
		self.widgetAliasMap[widgetId] = widgetAlias;
		const initModel = {
			header : initialModel.header,
			_env : env
		};
		const initSelModel = {
			header : initialSelModel.header,
			_env : env
		};
		self.statsModelMap.set(widgetId, StatsModel.create(initModel));
		self.selStatsModelMap.set(widgetId, SelectionStatsModel.create(initSelModel));
	},

	/**
	 * initialize 'synchronization' for a widget which is part of a compare card.
	 * When enabled from the menu, this widget will have synchronized crosshairs and synchronized actions(sort, topN, format)
	 * @param widgetApi - The widgetApi for a widget in this comparison.
	 */
	initializeSynch(widgetContentApi) {
		const widgetId = widgetContentApi.getId();
		//Synchronized actions
		self.synchActionProcessor.initialize(widgetContentApi);

		//Synchronized crosshairsFeature
		self._activateCrosshairs(widgetId);
	},

	initializeWidgetSelectionsSynced(eventGroups) {
		//This method is called whenever a new widgetAPI is added to the comparison.
		//Mark the widgetSelectionsSyncUpdated flag so that we recalculate (isWidgetSelectionsSynced)
		//which notifies the syncSelections toggle of the new comparison structure.
		self.setEventGroups(eventGroups);
		self.setWidgetSelectionsSyncUpdated();
	},

	initializeWidgetApis(widgetContentApi) {
		const widgetId = widgetContentApi.getId();
		if (!self.widgetIds.includes(widgetId)) {
			self.widgetIds.push(widgetId);
		}
		self.widgetContentApisMap[widgetId] = widgetContentApi;
	},

	beforeDestroy() {
		self.refChecker.remove();

		//If synchronized actions were initialized, make sure to cleanup the event handlers.
		self.synchActionProcessor.destroy();
		self._mobxDisposeEvents.forEach(dispose => dispose());

		self.compareTableSummary && destroy(self.compareTableSummary);
		self.compareTableSelectionSummary && destroy(self.compareTableSelectionSummary);
	},

	/**
	 * setup callbacks into widgets to get the stats
	 * @param {string} widgetId
	 * @param {Object} svc The host application service
	 */
	setupStatsModels(widgetId, summarizerFeature) {
		self.widgetId = widgetId;
		if (summarizerFeature && summarizerFeature.activate) {
			summarizerFeature.activate({
				cb: self.updateData.bind(self, self.refChecker, widgetId),
				cbSel: self.updateSelectionData.bind(self, self.refChecker, widgetId),
				cbDomain: self.updateDomain.bind(self, self.refChecker, widgetId),
				cbDeactivate: self.cleanData.bind(self, self.refChecker, widgetId)
			});
		}
	},

	/**
	 * If the crosshairs feature is enabled, setup callbacks into widgets needed when synchronizing crosshairs.
	 * @param {string} widgetId - the widgetId to activateCrosshairs for.
	 */
	_activateCrosshairs(widgetId) {
		const matchingWidgetContentApi = self.widgetContentApisMap[widgetId];
		const crosshairsFeature = matchingWidgetContentApi && matchingWidgetContentApi.getFeature('WidgetAPI.deprecated').getFeature('vis-crosshairs');
		crosshairsFeature && crosshairsFeature.activate({
			cbOnMoveCrosshairs: self.onMoveCrosshairs.bind(self, self.refChecker, widgetId)
		});
	},

	/**
	 * Track when a widget has moved its crosshairs and synchronize that move to other widgets in the comparison.
	 * @param refChecker - RefChecker
	 * @param sourceWidgetId - the sourceWidget...crosshairs need to be synched on other widgets only.
	 * @param data - where to move the crosshairs too.
	 */
	onMoveCrosshairs(refChecker, sourceWidgetId, data) {
		if (!refChecker.isValid(self)) {
			return;
		}
		//TODO: For now, we're on and synched (ie: default for compare)
		//When menu toggles for synchronize are available, this function can be modified.
		//(ie: only synch when synchCrosshairs is on)
		let widgetID;
		self.getWidgetContentApis().forEach((widgetContentApi) => {
			widgetID = widgetContentApi.getId();
			const crosshairsFeature=widgetContentApi.getFeature('WidgetAPI.deprecated').getFeature('vis-crosshairs');
			if (widgetID!==sourceWidgetId) {
				//NOTE: When a feature is disabled, getFeature should not return it.....we may be able to remove the enabled check.
				if (crosshairsFeature && crosshairsFeature.isActivated() && self.isCompareLineSynced()) {
					crosshairsFeature.renderCrosshairs(data.value);
				}
			}
			//if isCompareLineSynced() returns false then we render the popover only for the source widget
			//if isCompareLineSynced() returns true then we render the popover for both the compare cards
			if(self.isNewCompareLineFeatureEnabled()){
				const shouldSetupPopOver = self.isCompareLineSynced()? true: sourceWidgetId === widgetID;
				if(shouldSetupPopOver && crosshairsFeature) {
					self._setupPopover(crosshairsFeature, widgetID, data.value);
				}
			}
		});
		return;
	},

	/**
	 * Sets up the base for creating a popover by
	 * Setting the matching points when the compare line is moved and
	 * Setting a reference to the function to create the popover
	 */
	_setupPopover(crosshairsFeature, widgetContentID, value) {
		self.matchingPoints.set(widgetContentID, crosshairsFeature.getCrossHairMatchingPoints(value));
		if (!self.renderCrosshairPopOver) {
			self.renderCrosshairPopOver = crosshairsFeature.renderPopOver;
		}
	},

	/**
	 * updates default stats model on receiving stats from live widget
	 * @param {object} refChecker
	 * @param {string} widgetId
	 * @param {object} data
	 * @returns {Promise}
	 */
	updateData(refChecker, widgetId, data) {
		if (!refChecker.isValid(self)) {
			return;
		}
		const mdl = self.statsModelMap.get(widgetId);
		if (mdl) {
			mdl.updateData(data);
		}

		self.statsUpdated++;
		self._updateMatchingPoints(widgetId);

		self.compareTableSummary.computeSummaryInfo(self.statsModelMap, self.widgetAliasMap, self.widgetContentApisMap, widgetId);
		return self.populateWidgetFilterInfo(widgetId);
	},

	/**
	 * computes and updates domain on receiving stats from live widget
	 * @param {object} refChecker
	 * @param {string} widgetId
	 * @param {object} data
	 * @returns {Promise}
	 */
	updateDomain(refChecker, widgetId, data) {
		let result;
		if (!refChecker.isValid(self)) {
			return;
		}
		let sourceWidgetContentApi;
		const isMatchScaleForcedByUser = self.isToggledBySyncAxisButton && self.matchScale;
		self.getWidgetContentApis().forEach((widgetContentApi) => {
			if (widgetContentApi.getId() === widgetId) {
				sourceWidgetContentApi = widgetContentApi;
				result = self.theMatchScaleProcessor.getDomainAndNotifyWidgetId(data.measures, self.domain, widgetContentApi, self.widgetIds, isMatchScaleForcedByUser);
			}
		});
		/* once the user toggles the sync axis button, the choice is respected within the session */
		if (self.isToggledBySyncAxisButton) {
			if (self.matchScale) {
				self.domain = result.domain;
				return self.taskComplete(result.notifyWidgetId, widgetId /*sourceWidgetId*/);
			} else {
				return Promise.resolve(true);
			}
		} else {
			/**
			 * Before the user manually toggles the sync axis button, the sync axis scale button is
 				- on: if there is a same measure with same aggregation in the same slot
 				- off: if there is no same measure with same aggregation and matching axis scale does not make sense
			 */
			const defaultOption = self.getDefaultMatchScaleOption(result.domain, sourceWidgetContentApi);
			if (defaultOption === false) {
				self.setScaleSynced(false, false/*isToggledBySyncAxisButton*/);
				if(result.notifyWidgetId){
					return self.taskComplete(result.notifyWidgetId, widgetId /*sourceWidgetId*/);
				}
				return Promise.resolve(true);
			}
			self.setScaleSynced(true, false/*isToggledBySyncAxisButton*/);
			self.domain = result.domain;

			return self.taskComplete(result.notifyWidgetId, widgetId /*sourceWidgetId*/);
		}

	},

	/**
	 * Called when the vis-summarizer is deactivated, clean data related to widgetId
	 * @param {object} refChecker
	 * @param {string} widgetId the widgetId being removed
	 */
	cleanData(refChecker, widgetId) {
		if (!refChecker.isValid(self)) {
			return;
		}
		const widgetContentApi = self.widgetContentApisMap[widgetId];

		//--Remove widgetId from widgetIds, widgetContentApisMap, widgetAliasMap and widgetMeasureNameMap
		const indexofWidget = self.widgetIds.indexOf(widgetId);
		if(~indexofWidget){
			self.widgetIds.splice(indexofWidget, 1);
		}
		delete self.widgetAliasMap[widgetId];
		delete self.widgetContentApisMap[widgetId];
		delete self.widgetMeasureNameMap[widgetId];

		//--Remove widgetId from statsModelMap
		self.statsModelMap.delete(widgetId);
		self.compareTableSummary.computeSummaryInfo(self.statsModelMap, self.widgetAliasMap, self.widgetContentApisMap, widgetId);
		self.statsUpdated++;

		//--Remove widgetId from widgetFilterInfo
		delete self.widgetFilterInfo[widgetId];
		self.incrWidgetUpdatedCount();

		//--Update domain
		if (self.matchScale) {
			const result = self.theMatchScaleProcessor.getDomainAndNotifyWidgetId({}, self.domain, widgetContentApi, self.widgetIds);
			self.domain = result.domain;
			if (result.notifyWidgetId) {
				const otherWidgetContentApi = self.widgetContentApisMap[result.notifyWidgetId];
				const otherWidgetApi = otherWidgetContentApi.getFeature('WidgetAPI.deprecated');
				if(otherWidgetApi && otherWidgetApi.getVisApi){
					const otherWidgetVisApi = otherWidgetApi.getVisApi();
					otherWidgetVisApi && otherWidgetVisApi.reRender();
				}
			}
		}
		//Remove widgetId from domain
		if(self.domain){
			delete self.domain[widgetId];
		}

		//--Remove widgetId from selStatsModelMap
		self.selStatsModelMap.delete(widgetId);
		self.compareTableSelectionSummary.computeSummaryInfo(self.selStatsModelMap, self.widgetAliasMap, self.widgetContentApisMap, widgetId, 'selections');
		self.selectionUpdated++;
	},

	/**
	 * two widgets update data in compare stats model which can be synchronous
	 * we first check if the domain is fulfilled (i.e. the domain was updated by both widgets)
	 * 		-> if so, resolve the promise immediately with the domain
	 * 		-> if no, waits for 1 second and hopefully both stats have been updated in stats model then callback to stats feature which will re-render the other widget
	 * @param {string} notifyWidgetId
	 * @param {string} [sourceWidgetId] Optional - the source widget id
	 * @returns {Promise} resolved with the computed domain
	 */
	taskComplete(notifyWidgetId, sourceWidgetId = null) {
		const delay = 1000;
		const promise = new Promise(function (resolve) {
			/* no need to go through the rest of function if no domain is set, the domain is undefined in this case */
			if(!self.domain){
				resolve(self.domain);
				return;
			}

			const isDomainFullFilled = self.domain && Object.keys(self.domain).length > 1;
			if (!isDomainFullFilled) {
				if (self.delayingFastestRender) {
					self.delayingFastestRender = null;
					self._reRenderWidget(notifyWidgetId, resolve);
				} else {
					self.delayingFastestRender = setTimeout(() => {
						self._reRenderWidget(notifyWidgetId, resolve);
						/* if resize happens before setData task completes, resize can make the render sequence stale
						as a workaround, we are resizing the source widget before the resize is a render sequence task
						see defect */
						if(sourceWidgetId && self.widgetContentApisMap[sourceWidgetId]){
							self.widgetContentApisMap[sourceWidgetId].getFeature('WidgetAPI.deprecated').render({ resize: true });
						}
					}, delay);
				}
			} else {
				if (notifyWidgetId) {
					self._reRenderWidget(notifyWidgetId, resolve);
				} else {
					resolve(self.domain);
				}
			}
		});
		return promise;
	},

	/**
	 * updates selection stats model on receiving stats from live widget
	 */
	updateSelectionData(refChecker, widgetId, data) {
		if (!refChecker.isValid(self)) {
			return;
		}
		const mdl = self.selStatsModelMap.get(widgetId);
		mdl.updateData(data);
		self.selectionUpdated++;
		self.compareTableSelectionSummary.computeSummaryInfo(self.selStatsModelMap, self.widgetAliasMap, self.widgetContentApisMap, widgetId, 'selections');
	},

	setDashboardApi: function (dashboardApi) {
		self.dashboardApi = dashboardApi;
	},

	_reRenderWidget: function (widgetId, resolve) {
		resolve(self.domain);

		if (widgetId) {
			return self.dashboardApi.getCanvasWhenReady()
				.then(canvas => canvas.getWidgetWhenReady(widgetId))
				.then(widgetApi => {
					if (widgetApi) {
						// call a render only after the widget has completed the current render sequence
						return widgetApi.whenRenderComplete()
							.then(() => widgetApi.render());
					} else {
						return true;
					}
				});
		}
		return Promise.resolve(true);
	},

	/**
	 * update matching points with the latest matching points from crosshair feature
	 * @param {String} widgetId - the id of the widget
	 */
	_updateMatchingPoints: function(widgetContentId) {
		const widgetContentApi = self.widgetContentApisMap[widgetContentId];
		if (widgetContentApi && widgetContentApi.getFeature) {
			const widgetApi = widgetContentApi.getFeature('WidgetAPI.deprecated');
			if (widgetApi) {
				const crosshairsFeature = widgetApi.getFeature('vis-crosshairs');
				if (crosshairsFeature && crosshairsFeature.isSupported() && crosshairsFeature.isActivated()) {
					self._setupPopover(crosshairsFeature, widgetContentId, crosshairsFeature.getOptimalLineValue());
				}
			}
		}
	},

	populateWidgetFilterInfo(widgetContentId) {
		const widgetContent = self.widgetContentApisMap[widgetContentId];
		const widgetApi = widgetContent && widgetContent.getFeature('WidgetAPI.deprecated');
		if (widgetApi && widgetApi.whenRenderComplete) {
			return widgetApi.whenRenderComplete().then(function (){
				const visApi = widgetApi.getVisApi();
				const filterInfo = visApi.getLocalFilterAndTopBottomInfo();
				self.setWidgetFilterInfo( widgetContentId, filterInfo);
				self.incrWidgetUpdatedCount();
				return filterInfo;
			});
		} else {
			return Promise.resolve([]);
		}
	},

	setWidgetFilterInfo( widgetId, filterInfo) {
		self.widgetFilterInfo[widgetId] = filterInfo;
	},

	incrWidgetUpdatedCount() {
		self.widgetUpdatedCount++;
	}

}));



// WEBPACK FOOTER //
// ./src/js/features/compare/CompareModel.js